import { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import type {
  BrandCode,
  CaptureState,
  PaymentFilterOption,
  PaymentSearchByField,
  PaymentTypeKey,
  UiPaymentStatus,
} from '@models/payments';
import FilterDropdown from '@components/FilterDropdown';

import s from './PaymentsFilters.module.css';

type StatusFilter = UiPaymentStatus | 'all';
type TypeFilter = PaymentTypeKey | 'all';
type BrandCodeFilter = BrandCode | 'all';
type CaptureStateFilter = CaptureState | 'all';
type SearchByFilter = PaymentSearchByField | 'all';

type SearchFieldProps = {
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  searchBy: SearchByFilter;
  onSearchByChange: (v: SearchByFilter) => void;
  searchByOptions: PaymentFilterOption<PaymentSearchByField>[];
};

function SearchField({
  value,
  onChange,
  hasError,
  searchBy,
  onSearchByChange,
  searchByOptions,
}: SearchFieldProps) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const active = searchByOptions.find((o) => o.value === searchBy);
  const triggerLabel = active ? active.label : 'Select field';

  return (
    <div className={`${s.search} ${hasError ? s.searchInvalid : ''}`}>
      <div ref={pickerRef} className={s.searchByWrap}>
        <button
          type="button"
          className={`${s.searchByBtn} ${active ? s.searchByBtnActive : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Choose what to search by">
          <span className={s.searchByText}>{triggerLabel}</span>
          <KeyboardArrowDownIcon
            className={`${s.searchByChevron} ${open ? s.searchByChevronOpen : ''}`}
            sx={{ fontSize: 16 }}
          />
        </button>
        {open && (
          <div className={s.searchByMenu} role="listbox">
            {searchByOptions.map((o) => {
              const selected = o.value === searchBy;
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={selected}
                  className={`${s.menuItem} ${selected ? s.menuItemActive : ''}`}
                  onClick={() => {
                    onSearchByChange(o.value);
                    setOpen(false);
                  }}>
                  <span className={s.menuItemLabel}>{o.label}</span>
                  {selected && <CheckIcon className={s.menuCheck} sx={{ fontSize: 16 }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className={s.searchDivider} aria-hidden />
      <SearchIcon className={s.searchIcon} sx={{ fontSize: 18 }} />
      <input
        type="text"
        className={s.searchInput}
        placeholder={active ? `Search by ${active.label.toLowerCase()}…` : 'Search payments…'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={hasError ? 'true' : undefined}
      />
      {value && (
        <button
          type="button"
          className={s.searchClear}
          onClick={() => onChange('')}
          aria-label="Clear search">
          <CloseIcon sx={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );
}

type PaymentsFiltersProps = {
  search: string;
  onSearchChange: (v: string) => void;
  searchBy: SearchByFilter;
  onSearchByChange: (v: SearchByFilter) => void;
  searchByOptions: PaymentFilterOption<PaymentSearchByField>[];
  searchError: string | null;
  status: StatusFilter;
  onStatusChange: (v: StatusFilter) => void;
  type: TypeFilter;
  onTypeChange: (v: TypeFilter) => void;
  brandCode: BrandCodeFilter;
  onBrandCodeChange: (v: BrandCodeFilter) => void;
  captureState: CaptureStateFilter;
  onCaptureStateChange: (v: CaptureStateFilter) => void;
  fromDate: Date;
  onFromDateChange: (d: Date) => void;
  toDate: Date;
  onToDateChange: (d: Date) => void;
  statusOptions: PaymentFilterOption<UiPaymentStatus>[];
  typeOptions: PaymentFilterOption<PaymentTypeKey>[];
  brandCodeOptions: PaymentFilterOption<BrandCode>[];
  captureStateOptions: PaymentFilterOption<CaptureState>[];
  onClear: () => void;
};

function PaymentsFilters({
  search,
  onSearchChange,
  searchBy,
  onSearchByChange,
  searchByOptions,
  searchError,
  status,
  onStatusChange,
  type,
  onTypeChange,
  brandCode,
  onBrandCodeChange,
  captureState,
  onCaptureStateChange,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  statusOptions,
  typeOptions,
  brandCodeOptions,
  captureStateOptions,
  onClear,
}: PaymentsFiltersProps) {
  const hasActiveFilter =
    status !== 'all' || type !== 'all' || brandCode !== 'all' || captureState !== 'all';
  const today = new Date();

  return (
    <div className={s.toolbar}>
      <div className={s.toolbarLeft}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={fromDate}
            maxDate={toDate}
            onChange={(d) => d && onFromDateChange(d)}
            slotProps={{ textField: { size: 'small' as const, sx: { width: 150 } } }}
          />
          <DatePicker
            label="End Date"
            value={toDate}
            minDate={fromDate}
            maxDate={today}
            onChange={(d) => d && onToDateChange(d)}
            slotProps={{ textField: { size: 'small' as const, sx: { width: 150 } } }}
          />
        </LocalizationProvider>
        <FilterDropdown<UiPaymentStatus>
          label="Status"
          value={status}
          options={statusOptions}
          onChange={onStatusChange}
        />
        <FilterDropdown<PaymentTypeKey>
          label="Payment type"
          value={type}
          options={typeOptions}
          onChange={onTypeChange}
        />
        <FilterDropdown<BrandCode>
          label="Brand"
          value={brandCode}
          options={brandCodeOptions}
          onChange={onBrandCodeChange}
        />
        <FilterDropdown<CaptureState>
          label="Capture"
          value={captureState}
          options={captureStateOptions}
          onChange={onCaptureStateChange}
        />
        {hasActiveFilter && (
          <button type="button" className={s.clearBtn} onClick={onClear}>
            Clear
          </button>
        )}
      </div>

      <div className={s.searchSection}>
        <SearchField
          value={search}
          onChange={onSearchChange}
          hasError={!!searchError}
          searchBy={searchBy}
          onSearchByChange={onSearchByChange}
          searchByOptions={searchByOptions}
        />
        {searchError && (
          <span className={s.searchErrorMessage} role="alert">
            {searchError}
          </span>
        )}
      </div>
    </div>
  );
}

export default PaymentsFilters;
